import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Package, LogOut, User, Zap, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const loc = useLocation();

  const isActive = (path) => loc.pathname === path;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-zinc-900">
      {/* Live price ticker */}
      <div className="caution-stripe h-1.5 w-full" />
      <div className="px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="w-9 h-9 bg-[#FF5A00] flex items-center justify-center border-2 border-zinc-900 hard-shadow-sm">
            <Zap className="w-5 h-5 text-zinc-900" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-display text-xl font-black tracking-tight">
              UDYO<span className="text-[#FF5A00]">GG</span>
            </div>
            <div className="tag mt-0.5">Industrial Commerce · Live Inventory</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {[
            { to: "/", label: "DASHBOARD" },
            { to: "/catalog", label: "CATALOG" },
            { to: "/orders", label: "ORDERS" },
            ...(user?.role === "admin" ? [{ to: "/admin", label: "ADMIN" }] : []),
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-4 py-2 font-mono-data text-xs tracking-widest border-2 ${
                isActive(l.to)
                  ? "border-zinc-900 bg-zinc-900 text-white"
                  : "border-transparent hover:border-zinc-900"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => nav("/cart")}
            data-testid="header-cart-button"
            className="relative border-2 border-zinc-900 px-3 py-2 hover:bg-zinc-900 hover:text-white transition"
            aria-label="Cart"
          >
            <ShoppingCart className="w-4 h-4" strokeWidth={2} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 bg-[#FF5A00] text-white font-mono-data text-[10px] font-bold w-5 h-5 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/profile"
                data-testid="header-profile-link"
                className="hidden lg:flex flex-col items-end leading-tight border-l border-zinc-300 pl-3 hover:text-[#FF5A00] group"
              >
                <span className="text-[11px] font-mono-data text-zinc-500 group-hover:text-[#FF5A00] flex items-center gap-1">
                  {user.role === "admin" && <Shield className="w-3 h-3 text-[#FF5A00]" />}
                  +{user.phone}
                </span>
                <span className="text-sm font-medium truncate max-w-[140px]">
                  {user.name}
                </span>
              </Link>
              <Link
                to="/profile"
                data-testid="header-profile-icon"
                className="lg:hidden border-2 border-zinc-900 px-3 py-2 hover:bg-zinc-900 hover:text-white transition"
                aria-label="Profile"
              >
                <User className="w-4 h-4" strokeWidth={2} />
              </Link>
              <button
                onClick={() => {
                  logout();
                  nav("/login");
                }}
                data-testid="logout-button"
                className="border-2 border-zinc-900 px-3 py-2 hover:bg-zinc-900 hover:text-white transition"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="bg-[#FF5A00] text-white px-4 py-2 font-mono-data text-xs tracking-widest border-2 border-zinc-900 hard-shadow-sm hover:bg-[#e65000]"
            >
              LOGIN
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
